import $ from 'jquery';
import Slider from 'bootstrap-slider';
import {
    ATTR,
    CLASSNAMES,
    IDS,
    PRICE_RANGE_SLIDER_IDS,
    SELECTORS,
    TEXTS,
    escapeAttrValue
} from './filters.state';

/**
 * @typedef {Object} EventBinding
 * @property {Element|Document|Window|JQuery} target
 * @property {string} events
 * @property {string=} selector
 * @property {Function} handler
 */

/**
 * Привязать обработчики событий из декларативного списка.
 * @param {EventBinding[]} bindings
 */
export const bindEvents = bindings => {
    (bindings || []).forEach(({ target, events, selector, handler }) => {
        const $target = target && target.jquery ? target : $(target);
        if (selector) {
            $target.on(events, selector, handler);
        } else {
            $target.on(events, handler);
        }
    });
};

/**
 * Считать выбранные фильтры и диапазон цены из DOM.
 * @param {import('./filters.state').FilterState} state
 * @returns {{ selectedIds: string[], priceMin: number, priceMax: number }}
 */
export const parseFiltersFromDOM = state => {
    const selectedIds = new Set();

    $(SELECTORS.filterInputs).filter(':checked').each((_, el) => {
        const filterId = $(el).attr(ATTR.filterId);
        if (filterId) {
            selectedIds.add(filterId);
        }
    });

    $(SELECTORS.filterButtons).filter('.active').each((_, el) => {
        const filterId = $(el).attr(ATTR.filterId);
        if (filterId) {
            selectedIds.add(filterId);
        }
    });

    return {
        selectedIds: Array.from(selectedIds),
        priceMin: getFilterMinPrice(state),
        priceMax: getFilterMaxPrice(state)
    };
};

/**
 * Применить состояние фильтров к элементам DOM.
 * @param {import('./filters.state').FilterState} state
 */
export const applyFiltersToDOM = state => {
    if (!state) {
        return;
    }

    Object.keys(state.modifiedFilters || {}).forEach(filterId => {
        $(`input[${ATTR.filterId}="${filterId}"]`).prop('checked', true);
        $(`${SELECTORS.filtersModal} .btn[${ATTR.filterId}="${filterId}"]`).addClass(CLASSNAMES.activeButton);
    });
};

/**
 * Считать границы цены из data-атрибутов слайдера.
 * @returns {{ min: number, max: number }|null}
 */
export const readPriceBoundsFromDom = () => {
    let domMin = null;
    let domMax = null;

    PRICE_RANGE_SLIDER_IDS.some(id => {
        const el = $(`#${id}`);
        if (el.length === 0) {
            return false;
        }

        const minValue = parseInt(el.data('min-value'), 10);
        const maxValue = parseInt(el.data('max-value'), 10);

        if (!Number.isNaN(minValue) && !Number.isNaN(maxValue)) {
            domMin = minValue;
            domMax = maxValue;
            return true;
        }

        return false;
    });

    if (domMin !== null && domMax !== null) {
        return { min: domMin, max: domMax };
    }

    return null;
};

/**
 * Получить текущую минимальную цену из DOM, иначе из state.
 * @param {import('./filters.state').FilterState} state
 * @returns {number}
 */
export const getFilterMinPrice = state => {
    const value = $(SELECTORS.filterDesktopPriceMin).val();
    return value ? parseInt(value.replace(/\D/g, ''), 10) : state.priceMin;
};

/**
 * Получить текущую максимальную цену из DOM, иначе из state.
 * @param {import('./filters.state').FilterState} state
 * @returns {number}
 */
export const getFilterMaxPrice = state => {
    const value = $(SELECTORS.filterDesktopPriceMax).val();
    return value ? parseInt(value.replace(/\D/g, ''), 10) : state.priceMax;
};

/**
 * Получить текущий токен фильтра доставки из сайдбара. Доставка — radio с
 * name="filter[]" и маркером data-delivery-option; value содержит filter[]-токен
 * (пустой для «Не важно»). Сравнивается как строка в isFiltersUnchanged().
 * @returns {string}
 */
export const getCurrentDelivery = () => {
    const value = $(`${SELECTORS.filterForm} input[data-delivery-option]:checked`).val();
    return value ? String(value).trim() : '';
};

/**
 * Получить текущий выбор фильтра «Наличие на складах». Это мульти-селект (чекбоксы
 * name="filter[]" с маркером data-warehouse-option); value каждого — filter[]-токен.
 * Возвращает стабильную строку из отмеченных токенов (отсортированы), чтобы
 * сравнивать как строку в isFiltersUnchanged().
 * @returns {string}
 */
export const getCurrentWarehouse = () => {
    const values = $(`${SELECTORS.filterForm} input[data-warehouse-option]:checked`)
        .map((_, el) => String($(el).val()).trim())
        .get()
        .filter(value => value !== '');
    values.sort();
    return values.join(',');
};

/**
 * Получить стартовые значения слайдера из DOM.
 * @param {import('./filters.state').FilterState} state
 * @param {string} id
 * @returns {number[]}
 */
export const getRangeInitialValues = (state, id) => {
    const selector = `#${id}`;
    if ($(selector).length > 0) {
        return [
            $(selector).data('min-value') || state.priceMin,
            $(selector).data('max-value') || state.priceMax
        ];
    }

    return [state.priceMin, state.priceMax];
};

/**
 * Получить введённые значения из полей min/max.
 * @param {import('./filters.state').FilterState} state
 * @param {JQuery} inputWrapper
 * @returns {number[]}
 */
export const getRangeTypedValues = (state, inputWrapper) => {
    const typedValues = [0, 0];
    inputWrapper.find('> input').each(function() {
        const inputIndex = $(this).attr('id').match(/min/) ? 0 : 1;
        let inputValue = parseInt($(this).val().replace(/\D/g, ''), 10);
        inputValue = Math.round(inputValue / state.priceStep) * state.priceStep;
        typedValues[inputIndex] = Number.isNaN(inputValue) ? 0 : inputValue;
    });

    return typedValues;
};

/**
 * Инициализировать bootstrap-слайдеры для диапазона цены.
 * @param {import('./filters.state').FilterState} state
 * @param {(value: number[], id: string) => void} onSlide
 */
export const initRangeSliders = (state, onSlide) => {
    PRICE_RANGE_SLIDER_IDS.forEach(id => {
        const selector = `#${id}`;

        // Идемпотентность: слайдер для этого id уже создан — не инстанцируем повторно.
        // Нужно для доинициализации слайдера цены в ленивой модалке (её DOM появляется
        // позже первичного init), при этом повторный прогон не плодит дублирующую
        // разметку bootstrap-slider на уже готовых десктопных слайдерах.
        if (state.priceRangeSliders[id] || $(selector).length === 0) {
            return;
        }

        const minMaxValues = getRangeInitialValues(state, id);
        const rangeSlider = new Slider(selector, {
            min: minMaxValues[0],
            max: minMaxValues[1],
            range: true,
            value: minMaxValues,
            tooltip: 'hide',
            step: state.priceStep
        });

        state.priceRangeSliders[id] = rangeSlider;
        rangeSlider.on('slide', val => onSlide(val, id));
    });
};

/**
 * Обновить слайдеры и связанные поля после перемещения.
 * @param {import('./filters.state').FilterState} state
 * @param {number[]} val
 * @param {string} id
 */
export const syncRangeInputs = (state, val, id) => {
    Object.keys(state.priceRangeSliders).forEach(slid => {
        if (typeof state.priceRangeSliders[slid] === 'undefined') {
            return;
        }

        if (id !== slid) {
            state.priceRangeSliders[slid].setValue(val);
        }

        const rangeInputsWrap = $(`#${slid}`).siblings(`:has(${SELECTORS.rangeInput})`);
        rangeInputsWrap.find(SELECTORS.rangeInputMinValue).val(function() {
            return $(this).data('prefix') + val[0];
        });
        rangeInputsWrap.find(SELECTORS.rangeInputMaxValue).val(function() {
            return $(this).data('prefix') + val[1];
        });
    });
};

/**
 * Обработать движение слайдера и синхронизировать поля.
 * @param {import('./filters.state').FilterState} state
 * @param {number[]} val
 * @param {string} id
 * @param {(el: Element) => void} onSlideButton
 */
export const processRangeSlideAction = (state, val, id, onSlideButton) => {
    syncRangeInputs(state, val, id);

    if (onSlideButton) {
        onSlideButton($(SELECTORS.filterFormMaxValue).get(0));
    }

    updateMobileFilters(state, true );
};

/**
 * Обработать ввод цены и синхронизировать слайдеры.
 * @param {import('./filters.state').FilterState} state
 * @param {JQuery} elInput
 * @param {string|number} newValue
 * @param {() => void} onMobileUpdate
 * @returns {number}
 */
export const processFilterPriceInput = (state, elInput, newValue, onMobileUpdate) => {
    const isMin = elInput.attr('id').match(/min/);

    let otherInput = elInput.siblings();
    let otherInputValue = elInput.siblings().val().replace(/\D/g, '');

    if (!otherInputValue) {
        otherInputValue = isMin ? state.priceMax : state.priceMin;
    }

    const defaultValue = isMin ? state.priceMin : state.priceMax;
    newValue = Math.abs(parseInt(newValue, 10)) || defaultValue;

    const lowerThanMin = isMin && newValue < state.priceMin;
    const greaterThanMax = !isMin && newValue > state.priceMax;
    const minGreaterThanMax = isMin && newValue > otherInputValue;
    const maxLowerThanMin = !isMin && newValue < otherInputValue;

    if (lowerThanMin || greaterThanMax) {
        newValue = defaultValue;
    } else if (minGreaterThanMax || maxLowerThanMin) {
        newValue = otherInputValue;
    } else if (maxLowerThanMin) {
        otherInput.val(elInput.data('prefix') + state.priceMin);
    } else if (minGreaterThanMax) {
        otherInput.val(elInput.data('prefix') + state.priceMax);
    }

    const fullValue = elInput.data('prefix') + newValue;
    elInput.val(fullValue);
    elInput.selectionStart = elInput.selectionEnd;

    const typedSliderValues = getRangeTypedValues(state, elInput.parent());
    const rangeInputSel = `.range-input[id*="-${isMin ? 'min' : 'max'}-"]`;

    Object.keys(state.priceRangeSliders).forEach(slid => {
        if (typeof state.priceRangeSliders[slid] === 'undefined') {
            return;
        }
        state.priceRangeSliders[slid].setValue(typedSliderValues);
        const elPriceRangeInput = $(`#${slid}`)
            .siblings(`:has(${SELECTORS.rangeInput})`)
            .find(rangeInputSel);
        elPriceRangeInput.val(fullValue);
    });

    if (onMobileUpdate) {
        onMobileUpdate();
    }

    return newValue;
};

/**
 * Свернуть дублирующиеся подписи выбранных фильтров.
 */
export const collapseDuplicateFilterLabels = () => {
    const collAttr = ATTR.filtersSelectedCollapsed;
    $(`[${ATTR.filterType}]`).removeAttr(collAttr).each(function() {
        const currType = $(this).attr(ATTR.filterType);
        if ($(this).prevAll(`[${ATTR.filterType}="${escapeAttrValue(currType)}"]`).length > 0) {
            $(this).attr(collAttr, 1);
        }
    });
};

/**
 * Обновить бейджи мобильных фильтров и состояние кнопки цены.
 * @param {import('./filters.state').FilterState} state
 */
export const updateMobileFilters = ( state, slider = false ) => {
    const mobileFilters = $(SELECTORS.filtersCompact);

    ! slider && mobileFilters.find(`[${ATTR.filterCount}]`).each(function() {
        const filterType = $(this).attr(ATTR.filterType);
        const filterTypeCount = $(`${SELECTORS.filtersFull} [${ATTR.filterType}="${escapeAttrValue(filterType)}"]`).length;
        if (filterTypeCount > 0) {
            $(this).attr(ATTR.filterCount, filterTypeCount);
        } else {
            $(this).remove();
        }
    });

    let priceFilterActive = false;
    Object.keys(state.priceRangeSliders).forEach(slid => {
        if (typeof state.priceRangeSliders[slid] === 'undefined') {
            return;
        }
        $(`#${slid}`)
            .siblings(`:has(${SELECTORS.rangeInput})`)
            .find(SELECTORS.rangeInput)
            .each(function() {
                const defaultValue = $(this).attr('id').match(/-min-/)
                    ? state.priceMin
                    : state.priceMax;
                if ($(this).val() !== '' && $(this).val().replace(/\D/g, '') != defaultValue) {
                    priceFilterActive = true;
                }
            });
    });

    mobileFilters
        .find(SELECTORS.mobilePriceToggle)
        .toggleClass(CLASSNAMES.activeFilterButton, priceFilterActive);

    ! slider && mobileFilters
        .find(SELECTORS.mobileFiltersToggle)
        .toggleClass(
            CLASSNAMES.activeFilterButton,
            mobileFilters.find(`[${ATTR.filterType}].active`).length > 0
        );
};

/**
 * Обновить видимость кнопки «очистить всё».
 */
export const updateClearAllButton = () => {
    const fullFilters = $(SELECTORS.filtersFull);
    if (fullFilters.length > 0 && fullFilters.find(`.btn:not([${ATTR.clearAll}])`).length === 0) {
        fullFilters.find(`[${ATTR.clearAll}]`).remove();
    }
};

/**
 * Гарантировать наличие кнопки очистки фильтров.
 * @param {JQuery} elSelected
 * @param {(event: Event) => void} onClear
 */
export const addClearFiltersButton = (elSelected, onClear) => {
    const btnClearExisting = elSelected.find(`[${ATTR.clearAll}]`);
    const btnClearExists = btnClearExisting.length > 0;
    const btnClear = btnClearExists
        ? btnClearExisting
        : $('<button>')
            .attr(ATTR.clearAll, 1)
            .addClass(CLASSNAMES.clearButton)
            .text(TEXTS.clearAll);

    btnClear.off('click').on('click', onClear);
    if (!btnClearExists) {
        elSelected.append(btnClear);
    }
};

/**
 * Гарантировать наличие плавающей кнопки «Применить».
 */
export const addApplyFiltersButton = () => {
    const btnApplyExisting = $(`#${IDS.floatingSelectedSubmit}`);
    const btnApplyExists = btnApplyExisting.length > 0;
    const btnApply = btnApplyExists
        ? btnApplyExisting
        : $('<button>')
            .attr('id', IDS.floatingSelectedSubmit)
            .attr('type', 'submit')
            .attr('form', IDS.filterForm)
            .addClass(CLASSNAMES.applyButton)
            .text(TEXTS.apply)
            .css({ display: 'none' });

    if (!btnApplyExists) {
        $(SELECTORS.productListSettings).append(btnApply);
    }
};

/**
 * Получить плавающую кнопку отправки фильтра.
 * @returns {JQuery}
 */
export const getFloatingFilterSubmit = () => $(`#${IDS.floatingSubmit}`);

/**
 * Получить элементы форм с фильтрами
 * @returns {JQuery}
 */
export const getFilterForm = () => $( SELECTORS.filterForm );

/**
 * Получить плавающую кнопку «Применить».
 * @returns {JQuery}
 */
export const getFloatingSelectedSubmit = () => $(`#${IDS.floatingSelectedSubmit}`);

/**
 * Получить смещение фильтра в сайдбаре для плавающей кнопки.
 * @param {JQuery|Element} elFilter
 * @returns {number}
 */
export const getSidebarFilterOffset = ( elFilter, lastInteractedFilter = false ) => {
    let offsetDelta = 9;

    // Нормализуем вход в jQuery: HTMLElement/DOM-нода, HTML-строка ИЛИ строка-селектор
    // (напр. '#filter-warehouse' из initFilterAccordions). Без обработки строки-селектора
    // ниже падало `elFilter.is is not a function`, и исключение в jQuery-анимации
    // slideFilterFormButton прерывало transition collapse — сворачивание откатывалось.
    if( typeof elFilter === 'string' || elFilter instanceof HTMLElement || elFilter.nodeType === 1 ) {
        elFilter = $( elFilter );
    }

    if( typeof elFilter.is == 'undefined' ) {
        console.log( 'Error in sidebar offset filter: ', elFilter );
    }

    const formOffset = $(SELECTORS.filterFormWrapper).offset();
    const isAccordion = elFilter.is( SELECTORS.filterFormAccordions ); // Any accordion triggered
    const isSlide = elFilter.attr('id').match( /price/ );
    const isAccordionShown = isAccordion && elFilter.hasClass('show');
    const elAccordion = elFilter.closest( SELECTORS.accordionItem );
    const thisAccordionChanged = elAccordion.attr( ATTR.filterLastChanged ); // Clicked accordion changed
    const anyAccordionChanged = elAccordion.parent().find( `[${ATTR.filterLastChanged}]` ); // Clicked accordion changed
    const accordionChangedFilter = anyAccordionChanged.attr( ATTR.filterLastChanged );
    
    let elOffset = elFilter;
    if( isAccordion ) {
        const selFilter = `[${ATTR.filterId}="${accordionChangedFilter}"]`;
        if( thisAccordionChanged ) {
            elOffset = isAccordionShown ? elAccordion.find( selFilter ) : elAccordion;
        } else {
            elOffset = $( `${SELECTORS.filterForm} ${SELECTORS.accordionItem} ${selFilter}` );
        }        
    }

    const filterOffset = elOffset.offset();

    if( ! formOffset || ! filterOffset || ( isAccordion && ! accordionChangedFilter )) {
        return 0;
    }

    if( isSlide )
        offsetDelta += 5;

    if ( ! isAccordion && ! elFilter.hasClass(SELECTORS.formCheckInput.slice(1))) {
        offsetDelta += 20;
    }

    if( isAccordion && ! lastInteractedFilter )
        offsetDelta -= 5;

    return filterOffset.top - formOffset.top - offsetDelta;
};

/**
 * Сбросить фильтры в контейнере и слайдеры.
 * @param {import('./filters.state').FilterState} state
 * @param {JQuery} el
 */
export const resetFiltersUI = (state, el) => {
    el.find(SELECTORS.formCheckInput).prop('checked', false);
    el.find(SELECTORS.searchInput).val('');
    el.find(SELECTORS.listGroupItem).removeClass(`${CLASSNAMES.invisible} ${CLASSNAMES.visible}`);
    $(SELECTORS.mainContentSelectedButtons).remove();

    Object.keys(state.priceRangeSliders).forEach(slid => {
        const initSliderValues = getRangeInitialValues(state, slid);
        if (typeof state.priceRangeSliders[slid] !== 'undefined') {
            state.priceRangeSliders[slid].setValue(initSliderValues);
            $(`#${slid}-min-value, #${slid}-max-value`).val('');
        }
    });

    //getFloatingFilterSubmit().remove();
    updateMobileFilters(state);
};

/**
 * Отключить элементы управления фильтрами при отправке.
 * @param {JQuery} filterWrappers
 */
export const disableFilterControls = filterWrappers => {
    filterWrappers.find(SELECTORS.submitResetInputs).prop('disabled', true);
    filterWrappers
        .find(SELECTORS.filterControls)
        .addClass(CLASSNAMES.disabled);
};

/**
 * Определить контейнер выбранных фильтров для группы.
 * @param {{ selectedContainers?: string }} filter
 * @param {JQuery} elInput
 * @returns {JQuery}
 */
export const getFilterGroupSelectedContainer = (filter, elInput) => {
    const selectedContainerSet =
        typeof filter.selectedContainers !== 'undefined' &&
        $(filter.selectedContainers).length > 0;

    return selectedContainerSet
        ? $(filter.selectedContainers)
        : elInput.closest(SELECTORS.filterOptions).find(SELECTORS.filterSelected);
};

/**
 * Получить части заголовка выбранного фильтра.
 * @param {JQuery} elCheck
 * @returns {string[]}
 */
export const getSelectedFilterTitle = elCheck => {
    const nameParts = [];

    let filterType = String(elCheck.attr(ATTR.filterType) || '').trim();

    if (!filterType) {
        filterType = elCheck
            .first()
            .closest(SELECTORS.accordionItem)
            .find(SELECTORS.accordionButtonText)
            .text()
            .trim();
    }

    if (!filterType) {
        filterType = elCheck
            .closest(SELECTORS.modalContent)
            .find(SELECTORS.modalTitle)
            .text()
            .trim();
    }

    if (filterType) {
        nameParts.push(filterType);
        nameParts.push(elCheck
            .siblings(SELECTORS.formCheckLabel)
            .first()
            .text()
            .trim()
        );
    }

    return nameParts;
};

/**
 * Удалить все нецифровые символы из поля диапазона при keyup.
 * @param {HTMLInputElement} input
 */
export const sanitizeRangeInput = input => {
    input.value = input.value.replace(/\D+/g, '');
};

/**
 * Нормализовать текстовые поля с data-prefix.
 * @param {JQuery} form
 */
export const normalizePrefixedInputs = form => {
    form.find(SELECTORS.formControlWithPrefix).each(function() {
        if ($(this).val().match(/[0-9]/g)) {
            const numValue = parseInt($(this).val().replace(/\D/g, ''), 10);
            $(this).val(numValue);
        }
    });
};
